import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import apiService from '../services/apiService';

const JobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchJobDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getJobDetails(id);
      
      // Transform API response to match component structure
      const transformedJob = {
        id: response.id,
        jobId: response.job_id,
        title: response.title,
        company: response.company,
        location: response.location,
        type: response.type,
        date: response.posted_date,
        overview: response.overview,
        description: response.description,
        responsibilities: response.key_responsibilities || [],
        qualifications: response.qualifications || [],
        remuneration: response.remuneration,
        whyJoinUs: response.why_join_us,
        isActive: response.is_active
      };
      
      setJobData(transformedJob);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchJobDetails();
  }, [fetchJobDetails]);

  // Use jobData if available, otherwise show loading/error states
  const currentJobData = jobData;

  const handleApplyNow = () => {
    navigate(`/job-application/${id}`);
  };

  const handleBackToCareers = () => {
    navigate('/career');
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Loading job details...</h3>
          <p className="text-gray-500">Please wait while we fetch the job information.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading job details</h3>
          <p className="text-gray-500 mb-6">{error}</p>
          <div className="flex space-x-3 justify-center">
            <button
              onClick={fetchJobDetails}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={handleBackToCareers}
              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
            >
              Back to Careers
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No job data
  if (!currentJobData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Job not found</h3>
          <p className="text-gray-500 mb-6">The job you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={handleBackToCareers}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Back to Careers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{currentJobData.title} - Hospital Build Pvt Ltd</title>
        <meta name="description" content={`Apply for ${currentJobData.title} position at Hospital Build Pvt Ltd. ${currentJobData.overview}`} />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Back Button */}
          <button 
            onClick={handleBackToCareers}
            className="text-[#2C2C90] hover:text-[#1A1A5C] font-semibold mb-8 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Careers Page
          </button>

          {/* Job Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-4">                
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{currentJobData.title}</h1>
                  <div className="flex items-center space-x-6 text-gray-600 mb-4">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                      {currentJobData.type}
                    </div>
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                      </svg>
                      {currentJobData.location}
                    </div>
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                      </svg>
                      {currentJobData.date}
                    </div>
                  </div>
                  {/* <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center mr-3">
                      <span className="text-blue-600 font-bold text-sm">S</span>
                    </div>
                    <span className="text-gray-700 font-medium">{jobData.company}</span>
                  </div> */}
                </div>
              </div>
            </div>
          </div>

          {/* Job Content */}
          <div className="space-y-8">
            {/* Overview */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Overview</h2>
              <p className="text-gray-700 leading-relaxed">{currentJobData.overview}</p>
            </section>

            {/* Key Responsibilities */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Key Responsibilities</h2>
              {currentJobData.responsibilities.map((section, index) => (
                <div key={index} className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">{section.category}</h3>
                  <ul className="space-y-2">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start text-gray-700">
                        <span className="text-[#2C2C90] mr-2 mt-1">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </section>

            {/* Qualifications & Experience */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Qualifications & Experience</h2>
              <ul className="space-y-2">
                {currentJobData.qualifications.map((qualification, index) => (
                  <li key={index} className="flex items-start text-gray-700">
                    <span className="text-[#2C2C90] mr-2 mt-1">•</span>
                    {qualification}
                  </li>
                ))}
              </ul>
            </section>

            {/* Remuneration */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Remuneration</h2>
              <p className="text-gray-700">{currentJobData.remuneration}</p>
            </section>

            {/* Why Join Us */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Why Join Us</h2>
              <p className="text-gray-700 leading-relaxed">{currentJobData.whyJoinUs}</p>
            </section>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mt-12">
            <button
              onClick={handleApplyNow}
              className="bg-[#2C2C90] text-white px-8 py-3 rounded-lg hover:bg-[#1A1A5C] transition-colors font-semibold flex items-center justify-center flex-1"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              Apply Now
            </button>
            <button
              onClick={handleBackToCareers}
              className="bg-gray-600 text-white px-8 py-3 rounded-lg hover:bg-gray-700 transition-colors font-semibold flex items-center justify-center flex-1"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Careers Page
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default JobDetails;
